import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router';
import { CLIENT_ROUTES } from '../../shared/constants/routes.constants';
import { generatePath } from '../../shared/services/app.shared-service';
import NetworkBanner from './components/custom/NetworkBanner';
import Loader from './components/generic/Loader';
import AuthenticatedRoute from './components/routes/AuthenticatedRoute';
import MainRoute from './components/routes/MainRoute';
import RouteWithAdminHeader from './components/routes/RouteWithAdminHeader';
import RouteWithFooter from './components/routes/RouteWithFooter';
import RouteWithHeader from './components/routes/RouteWithHeader';
import ScrollToTop from './components/routes/ScrollToTop';
import NotFoundPage from './pages/NotFoundPage';
import { useSettings } from './services/settings.api';
import { useAuthStore } from './stores/useAuthStore';

import './app.css';
import { getImageUrl } from './utils/cloudinary.utils';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Auth'));
const Categories = lazy(() => import('./pages/admin/Categories'));
const Coupons = lazy(() => import('./pages/admin/Coupons'));
const Shipments = lazy(() => import('./pages/admin/Shipments'));
const CategoryProducts = lazy(() => import('./pages/CategoryProducts'));
const Products = lazy(() => import('./pages/admin/Products'));
const ProductView = lazy(() => import('./pages/ProductView'));
const ProductCreate = lazy(() => import('./pages/admin/ProductCreate'));
const ProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const FAQ = lazy(() => import('./pages/Faq'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages//admin/Settings'));
const Cart = lazy(() => import('./pages/Cart'));
const AllProducts = lazy(() => import('./pages/AllProducts'));
const RecommendedProducts = lazy(() => import('./pages/RecommendedProducts'));
const Sale = lazy(() => import('./pages/Sale'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Users = lazy(() => import('./pages/admin/Users'));
const Orders = lazy(() => import('./pages/admin/Orders'));
const Checkout = lazy(() => import('./pages/Checkout'));
const ArchitectAndDesigners = lazy(
	() => import('./pages/ArchitectAndDesigners')
);

function App() {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

	const { data: settings } = useSettings();

	useEffect(() => {
		function setFavicon(url: string) {
			let link: HTMLLinkElement | null =
				document.querySelector("link[rel*='icon']");

			if (!link) {
				link = document.createElement('link');
				link.rel = 'icon';
				document.head.appendChild(link);
			}

			link.href = url;
		}
		if (settings?.name) {
			document.title = settings?.name || 'Shop Here';
		}
		if (settings?.logoId) {
			setFavicon(getImageUrl(settings?.logoId) as string);
		}
	}, [settings]);

	return (
		<>
			<NetworkBanner />
			<Suspense fallback={<Loader />}>
				<main>
					<ScrollToTop />
					<Routes>
						<Route element={<RouteWithHeader />}>
							<Route element={<RouteWithFooter />}>
								<Route element={<MainRoute />}>
									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.HOME],
										})}
										element={<Home />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRODUCTS.BASE,
											],
										})}
										element={<AllProducts />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRODUCTS.BASE,
												CLIENT_ROUTES.PRODUCTS
													.RECOMMENDED,
											],
										})}
										element={<RecommendedProducts />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRODUCTS.BASE,
												CLIENT_ROUTES.PRODUCTS.SALE,
											],
										})}
										element={<Sale />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRODUCTS.BASE,
												CLIENT_ROUTES.PRODUCTS
													.CATEGORY_PRODUCTS,
											],
										})}
										element={<CategoryProducts />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRODUCT_DETAILS,
											],
										})}
										element={<ProductView />}
									/>
									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.ABOUT_US],
										})}
										element={<AboutUs />}
									/>
									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.QNA],
										})}
										element={<FAQ />}
									/>
									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.CONTACT],
										})}
										element={<ContactUs />}
									/>

									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.CART],
										})}
										element={<Cart />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.PRIVACY_POLICY,
											],
										})}
										element={<Privacy />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.TERMS_OF_SERVICE,
											],
										})}
										element={<Terms />}
									/>

									<Route
										path={generatePath({
											route: [CLIENT_ROUTES.CHECKOUT],
										})}
										element={<Checkout />}
									/>
									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ARCHITECTS_DESIGNERS,
											],
										})}
										element={<ArchitectAndDesigners />}
									/>
								</Route>
							</Route>
							<Route element={<MainRoute />}>
								<Route
									path={generatePath({
										route: [CLIENT_ROUTES.AUTH],
									})}
									element={<Login />}
								/>

								<Route
									path={generatePath({
										route: [CLIENT_ROUTES.FORGOT_PASSWORD],
									})}
									element={<ForgotPassword />}
								/>

								<Route
									path={generatePath({
										route: [CLIENT_ROUTES.RESET_PASSWORD],
									})}
									element={<ResetPassword />}
								/>

								<Route
									path={generatePath({
										route: [CLIENT_ROUTES.VERIFY_EMAIL],
									})}
									element={<VerifyEmail />}
								/>
							</Route>
						</Route>
						<Route
							element={<AuthenticatedRoute />}
							path={generatePath({
								route: [CLIENT_ROUTES.ADMIN.BASE, '/*'],
							})}>
							{isAuthenticated && (
								<Route element={<RouteWithAdminHeader />}>
									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.CATEGORIES,
											],
											relativePath: true,
										})}
										element={<Categories />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.COUPONS,
											],
											relativePath: true,
										})}
										element={<Coupons />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.SHIPMENTS,
											],
											relativePath: true,
										})}
										element={<Shipments />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.BASE,
											],
											relativePath: true,
										})}
										element={<Products />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.BASE,
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.VIEW,
											],
											relativePath: true,
										})}
										element={<ProductView />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.BASE,
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.CREATE,
											],
											relativePath: true,
										})}
										element={<ProductCreate />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.BASE,
												CLIENT_ROUTES.ADMIN.ROUTES
													.PRODUCTS.EDIT,
											],
											relativePath: true,
										})}
										element={<ProductEdit />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.PROFILE,
											],
											relativePath: true,
										})}
										element={<Profile />}
									/>
									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.SETTINGS,
											],
											relativePath: true,
										})}
										element={<Settings />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.USERS,
											],
											relativePath: true,
										})}
										element={<Users />}
									/>

									<Route
										path={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.ROUTES
													.ORDERS,
											],
											relativePath: true,
										})}
										element={<Orders />}
									/>

									<Route
										path="*"
										element={<NotFoundPage />}
									/>
								</Route>
							)}
						</Route>
						<Route path="*" element={<RouteWithHeader />}>
							<Route element={<RouteWithFooter />}>
								<Route element={<MainRoute />}>
									<Route
										path="*"
										element={<NotFoundPage />}
									/>
								</Route>
							</Route>
						</Route>
					</Routes>
				</main>
			</Suspense>
		</>
	);
}

export default App;
